import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CitationChip } from '@/components/app/CitationChip';
import { AskAboutButton } from '@/components/app/AskAboutButton';

/**
 * Shared footer for every agent result (Compose / Brief / Audit / Compare).
 * Turns a produced output into a launch point: clickable source citations,
 * an "Ask a follow-up" handoff, a link to where it's saved, and a slot for
 * the page's SaveResultButton (passed as children).
 */
export function ResultFooter({
  wsId,
  citedChunkIds = [],
  askSeed,
  children,
}: {
  wsId: string;
  citedChunkIds?: (string | number)[];
  askSeed?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-3 pt-3 border-t border-ink-100 space-y-2.5">
      {citedChunkIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-ink-500">
            {t('common.sources', { defaultValue: 'Sources' })}
          </span>
          {citedChunkIds.map((id) => (
            <CitationChip key={String(id)} wsId={wsId} chunkId={id} />
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {askSeed && (
            <AskAboutButton
              wsId={wsId}
              seed={askSeed}
              label={t('common.ask_followup', { defaultValue: 'Ask a follow-up' }) as string}
            />
          )}
          <Link
            to={`/app/workspace/${wsId}/results`}
            className="text-[12px] text-accent-600 hover:underline"
          >
            {t('common.view_in_results', { defaultValue: 'View in AI Results' })}
          </Link>
        </div>
        {children}
      </div>
    </div>
  );
}
